import { ButtonBase, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { saveBoosterProjectTextVariant } from 'API/booster-project';
import { AWTypes, MappedGenres, Routes } from 'enums';
import { usePrompt } from 'hooks/use-prompt';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import { useLocation, useNavigate } from 'react-router';
import useErrorStore from 'stores/error-store';
import { makeStyles } from 'tss-react/mui';
import AnalysisResults from '../features/augmented-writing/analysis-results/analysis-results';
import CustomQuillEditor from '../features/augmented-writing/editor/custom-quill-editor';
import EditorActionBar from '../features/augmented-writing/editor/editor-action-bar';
import { useInitiateEditor } from '../features/augmented-writing/editor/use-initiate-editor';
import Suggestions from '../features/augmented-writing/suggestions/suggestion-list';
import { useAugmentedWriting } from '../features/augmented-writing/use-augmented-writing';
import { useQueryProject } from '../features/augmented-writing/use-query-project';
import PaiButton from '../features/ui/base-components/pai-button';
import PaiSuccessDialog from '../features/ui/base-dialogs/pai-succes-dialog';

type PanelTab = 'suggestions' | 'result';

const AugmentedWriting: React.FC = () => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const { role } = useUserData();
  const navigate = useNavigate();
  const location = useLocation();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const theme = useTheme();
  const downXL = useMediaQuery(theme.breakpoints.down('xl'));
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));
  const downSM = useMediaQuery(theme.breakpoints.down(700));

  const editorRef = useRef<ReactQuill>(null);
  const qEditor = !_.isNil(editorRef) ? (editorRef as React.MutableRefObject<ReactQuill>).current?.getEditor() : null;

  const project = useQueryProject();

  const profileName = project?.projectName || undefined;
  const text = project?.text || '';
  const domain: AWTypes = !_.isNil(project) ? MappedGenres[project.genre || 'default'] : AWTypes.CUSTOMER;
  const profile = project?.profile || undefined;
  const profileId = project?._id || undefined;
  const occupation = project?.occupation || undefined;

  const [savedSuccessful, setSavedSuccessful] = useState<boolean | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [activeTab, setActiveTab] = useState<PanelTab>('suggestions');

  const {
    scores,
    userAssessment,
    highlights,
    sidebarNotes,
    grammarReplacements,
    paraphrases,
    allSuggestions,
    analysisLoading,
    skipSuggestion,
    ignoreSuggestion,
    insertAtPosition,
    analyzeText,
    updateSuggestionsFromEditor,
    selectActiveSuggestion,
    dynamicStyles,
    clickedChartCategoryKey,
    clickedChartCategory,
    clickedSidebarNotesCategory,
    onChartCategoryClicked,
    onSidebarNotesCategoryClicked,
    highlightTexts,
    modifiedHighlightTexts,
    userDone,
    checkIfEmpty,
    emptyEditor,
    activateSuggestionCard,
    isSuggestionActive,
    successDialog,
    closeSuccessDialog,
    resetEditorHighlights,
    analyzeTextQuick,
    optimizationMatch,
    optimizationLoading,
    autoFormatLoading,
  } = useAugmentedWriting(qEditor, domain, profile, profileId, occupation);
  useInitiateEditor(qEditor, text);

  const loading = analysisLoading || optimizationLoading || autoFormatLoading;

  const [pageLeaveReason, setPageLeaveReason] = useState<string>();

  const isFormDirty = () => {
    return !emptyEditor && _.isNil(pageLeaveReason);
  };

  usePrompt(t('pageLeaveWarning'), isFormDirty());

  useEffect(() => {
    if (!_.isNil(pageLeaveReason)) {
      navigate(pageLeaveReason);
    }
  }, [pageLeaveReason]);

  const isAnalysisPossible = () => {
    return !emptyEditor && !_.isNil(profile) && !loading;
  };

  const saveProjectText = async (projectId: string, text: string, score: number | undefined, finished: boolean) => {
    const res = await saveBoosterProjectTextVariant(projectId, text, score);

    if (res && !res.error) {
      if (finished) {
        const page = location.pathname.includes(Routes.HR_BOOSTER)
          ? Routes.HR_BOOSTER_DONE
          : location.pathname.includes(Routes.LEAD_BOOSTER)
          ? Routes.LEAD_BOOSTER_DONE
          : Routes.HR_BOOSTER_DONE;
        setPageLeaveReason(`${page}?project=${projectId}`);
      } else {
        setSavedSuccessful(true);
        setLastSavedAt(new Date());
        setTimeout(() => {
          setSavedSuccessful(null);
        }, 1500);
      }
    } else {
      handleOpenErrorDialog(res?.error, '/projects/boosters/:projectId', 'put');
    }
  };

  const handleSaveCurrentText = (finished: boolean) => {
    return async () => {
      const projectId = project?._id;

      const score = scores?.overall;
      const text = !_.isNil(qEditor) ? qEditor.getText() : '';

      if (!_.isNil(projectId)) {
        saveProjectText(projectId, text, score, finished);
      }
    };
  };

  const headerMeta = () => {
    const parts: string[] = [];

    if (!_.isNil(lastSavedAt)) {
      parts.push(t('lastSavedAt', { time: lastSavedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) }));
    }

    if (!_.isNil(occupation)) {
      parts.push(occupation);
    }

    return parts.length > 0 ? parts.join(' · ') : undefined;
  };

  const editorStatus = () => {
    if (loading) {
      return t('analysisRunning');
    }

    if (_.isNil(scores)) {
      return t('analysisNotRunYet');
    }

    return `${t('analysisUpToDate')} · ${t('paiMatch')} ${(scores.overall * 100).toFixed(0)} %`;
  };

  const selectTab = (tab: PanelTab) => {
    return () => setActiveTab(tab);
  };

  return (
    <Stack className={classes.textOptimization} direction={{ xs: 'column', lg: 'row' }} gap={'20px'}>
      <Stack className={classes.mainZone} direction={'column'} gap={'16px'}>
        <EditorActionBar
          profileName={profileName}
          metaText={headerMeta()}
          role={role}
          savedSuccessful={savedSuccessful}
          analyzeText={analyzeText}
          handleSaveCurrentText={handleSaveCurrentText}
          isAnalysisPossible={isAnalysisPossible}
          analyzeTextQuick={analyzeTextQuick}
        />
        <div className={classes.editorCard}>
          <div className={classes.editorContainer}>
            <CustomQuillEditor
              ref={editorRef}
              highlights={highlights}
              grammarReplacements={grammarReplacements}
              paraphrases={paraphrases}
              loading={loading}
              height={downLG ? 400 : undefined}
              dynamicStyles={dynamicStyles}
              highlightTexts={modifiedHighlightTexts ? modifiedHighlightTexts : highlightTexts}
              optimizationMatch={optimizationMatch}
              clickedChartCategory={clickedChartCategory}
              clickedSidebarNotesCategory={clickedSidebarNotesCategory}
              updateSuggestionsFromEditor={updateSuggestionsFromEditor}
              selectActiveSuggestion={selectActiveSuggestion}
              checkIfEmpty={checkIfEmpty}
              insertAtPosition={insertAtPosition}
              skipSuggestion={skipSuggestion}
              ignoreSuggestion={ignoreSuggestion}
            />
          </div>
          <Stack className={classes.editorFooter} direction={'row'} alignItems={'center'} gap={'16px'}>
            <Typography className={classes.editorStatus}>{editorStatus()}</Typography>
            <PaiButton
              text={downSM || downXL ? t('saveTextButtonTextShort') : t('saveTextButtonText')}
              action={handleSaveCurrentText(true)}
              disabled={!isAnalysisPossible()}
              noLineBreak={true}
              style={{ height: '44px', padding: '0 28px', borderRadius: '8px', fontSize: '1rem', marginLeft: 'auto' }}
            />
          </Stack>
        </div>
      </Stack>
      <div className={classes.sidePanel}>
        <div className={classes.tabBar}>
          <ButtonBase className={cx(classes.tab, activeTab === 'suggestions' ? 'active' : null)} onClick={selectTab('suggestions')}>
            {t('suggestions')}
            {allSuggestions.length > 0 ? <span className={classes.tabCount}>{allSuggestions.length}</span> : null}
          </ButtonBase>
          <ButtonBase className={cx(classes.tab, activeTab === 'result' ? 'active' : null)} onClick={selectTab('result')}>
            {t('result')}
          </ButtonBase>
        </div>
        <div className={classes.panelContent}>
          {activeTab === 'suggestions' ? (
            <Suggestions
              allSuggestions={allSuggestions}
              sidebarNotes={sidebarNotes}
              loading={loading}
              skipSuggestion={skipSuggestion}
              ignoreSuggestion={ignoreSuggestion}
              insertAtPosition={insertAtPosition}
              onSidebarNotesCategoryClicked={onSidebarNotesCategoryClicked}
              isSuggestionActive={isSuggestionActive}
              activateSuggestionCard={activateSuggestionCard}
              resetEditorHighlights={resetEditorHighlights}
            />
          ) : (
            <AnalysisResults
              scores={scores}
              userAssessment={userAssessment}
              contactAssessmentName={!_.isNil(project) ? project.assessment : undefined}
              loading={loading}
              clickedChartCategoryKey={clickedChartCategoryKey}
              userDone={!!userDone}
              profileName={project?.occupation}
              variant={'panel'}
              onChartCategoryClicked={onChartCategoryClicked}
            />
          )}
        </div>
      </div>
      <PaiSuccessDialog open={successDialog.open} content={successDialog.content} handleConfirm={closeSuccessDialog} />
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  textOptimization: {
    width: '100%',
    height: '100%',
    minHeight: '800px',
    padding: '20px',
    background: theme.palette.backgrounds.main_new,
    [theme.breakpoints.down('lg')]: {
      minHeight: 'unset',
      height: 'fit-content',
      padding: '16px',
    },
  },
  mainZone: {
    flex: 1,
    minWidth: 0,
    height: '100%',
    [theme.breakpoints.down('lg')]: {
      width: '100%',
      height: 'unset',
    },
  },
  editorCard: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: '#FFF',
    borderRadius: '12px',
    [theme.breakpoints.down('lg')]: {
      flex: 'unset',
    },
  },
  editorContainer: {
    flex: 1,
    minHeight: 0,
    '& .ql-container .ql-toolbar, & .ql-toolbar': {
      border: 'none',
      borderBottom: `1px solid ${theme.palette.greys.lighter}`,
      padding: '8px 20px',
    },
    '& .ql-container': {
      border: 'none',
      borderBottom: 'none',
    },
    '& .ql-container .ql-editor': {
      padding: '28px 40px',
      fontSize: theme.typography.pxToRem(16),
      lineHeight: 1.75,
      maxWidth: '820px',
      [theme.breakpoints.down('lg')]: {
        padding: '20px 24px',
      },
    },
  },
  editorFooter: {
    padding: '16px 20px',
    borderTop: `1px solid ${theme.palette.greys.lighter}`,
  },
  editorStatus: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  sidePanel: {
    width: '400px',
    flexShrink: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: '#FFF',
    borderRadius: '12px',
    [theme.breakpoints.down('xl')]: {
      width: '340px',
    },
    [theme.breakpoints.down('lg')]: {
      width: '100%',
      height: 'unset',
      maxHeight: '750px',
    },
  },
  tabBar: {
    display: 'flex',
    gap: '4px',
    padding: '8px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
  },
  tab: {
    flex: 1,
    justifyContent: 'center',
    gap: '6px',
    padding: '10px',
    borderRadius: '8px',
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 400,
    color: theme.palette.greys.dark,
    textTransform: 'none',
    '&:hover': {
      background: theme.palette.backgrounds.main_new,
    },
    '&.active': {
      background: theme.palette.backgrounds.main,
      color: theme.palette.primary.main,
      fontWeight: 500,
    },
  },
  tabCount: {
    opacity: 0.7,
  },
  panelContent: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
}));

export default AugmentedWriting;
